import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList, Search, Trash2, RefreshCw, Loader2, Copy,
  Filter, FileText, Pencil, CheckCircle2, ChevronDown, ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MiniAssignments = () => {
  // Tasks browsing state
  const [allTasks, setAllTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [taskSearch, setTaskSearch] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  // Create assignment state
  const [childTaskType, setChildTaskType] = useState("");
  const [assignedEmail, setAssignedEmail] = useState("");
  const [customRate, setCustomRate] = useState("");
  const [creating, setCreating] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);

  // Config
  const [taskTypes, setTaskTypes] = useState([]);
  const [emailRates, setEmailRates] = useState({});

  // Existing mini assignments
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [assignFilter, setAssignFilter] = useState({ task_type: "", search: "" });

  // Delete & Edit state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Section collapse
  const [tasksExpanded, setTasksExpanded] = useState(true);
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(true);

  // Load all data
  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const res = await axios.get(`${API}/tasks?limit=10000`);
      setAllTasks(res.data.tasks || []);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setTasksLoading(false);
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const res = await axios.get(`${API}/assignments`);
      setAssignments(res.data.assignments || []);
    } catch {
      toast.error("Failed to load mini assignments");
    } finally {
      setAssignmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const typesRes = await axios.get(`${API}/assignments/task-types`).catch(() => ({ data: { task_types: [], email_rates: {} } }));
        setTaskTypes(typesRes.data.task_types || []);
        setEmailRates(typesRes.data.email_rates || {});
      } catch {}
    };
    loadConfig();
    fetchTasks();
    fetchAssignments();
  }, [fetchTasks, fetchAssignments]);

  // Extract unique resource emails from tasks
  const resourceEmails = React.useMemo(() => {
    const map = {};
    allTasks.forEach(t => {
      if (t.resource && !map[t.resource]) {
        map[t.resource] = t.resource;
      }
    });
    return Object.keys(map).sort();
  }, [allTasks]);

  // Filter tasks
  const filteredTasks = allTasks.filter(t => {
    if (taskTypeFilter && (t.task || "") !== taskTypeFilter) return false;
    if (taskSearch) {
      const s = taskSearch.toLowerCase();
      return (t.main_title || "").toLowerCase().includes(s)
        || (t.episode_title || "").toLowerCase().includes(s)
        || (t.po_number || "").toLowerCase().includes(s)
        || (t.atlas_job_id || "").toLowerCase().includes(s)
        || (t.resource || "").toLowerCase().includes(s);
    }
    return true;
  });

  const allTaskTypes = [...new Set(allTasks.map(t => t.task || "").filter(Boolean))].sort();

  // Selection
  const toggleTask = (id) => {
    setSelectedTaskIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAllVisible = (checked) => {
    if (checked) {
      setSelectedTaskIds(prev => [...new Set([...prev, ...filteredTasks.map(t => t.id)])]);
    } else {
      const visibleIds = new Set(filteredTasks.map(t => t.id));
      setSelectedTaskIds(prev => prev.filter(id => !visibleIds.has(id)));
    }
  };

  const allVisibleSelected = filteredTasks.length > 0 && filteredTasks.every(t => selectedTaskIds.includes(t.id));

  // Rate lookup
  const getDefaultRate = (email, taskType) => {
    if (emailRates[email] && emailRates[email][taskType]) return emailRates[email][taskType];
    if (emailRates.__defaults__ && emailRates.__defaults__[taskType]) return emailRates.__defaults__[taskType].rate || 0;
    return 0;
  };

  const effectiveRate = customRate !== "" ? parseFloat(customRate) || 0 : getDefaultRate(assignedEmail, childTaskType);

  // Show create panel when tasks selected
  useEffect(() => {
    if (selectedTaskIds.length > 0) setShowCreatePanel(true);
  }, [selectedTaskIds]);

  // Create mini assignments
  const handleCreate = async () => {
    if (selectedTaskIds.length === 0) { toast.error("Select at least one task"); return; }
    if (!childTaskType) { toast.error("Select a child task type"); return; }
    if (!assignedEmail) { toast.error("Select a resource"); return; }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/assignments/bulk`, {
        parent_task_ids: selectedTaskIds,
        task_type: childTaskType,
        assigned_email: assignedEmail,
        rate: effectiveRate,
        rate_type: "per_unit",
        task_status: "Completed",
        invoice_status: "not_ready",
        override_duplicate: false,
      });
      const created = res.data.created_count || 0;
      const skipped = res.data.skipped_count || 0;
      if (created > 0) toast.success(`Created ${created} mini assignment(s)${skipped > 0 ? `, ${skipped} skipped (duplicates)` : ""}`);
      else if (skipped > 0) toast.info(`All ${skipped} skipped — duplicates already exist`);
      setSelectedTaskIds([]);
      setChildTaskType("");
      setCustomRate("");
      setShowCreatePanel(false);
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/assignments/${deleteTarget.id}`);
      toast.success("Deleted");
      setDeleteTarget(null);
      fetchAssignments();
    } catch { toast.error("Failed to delete"); }
  };

  // Edit
  const openEdit = (a) => {
    setEditTarget(a);
    setEditForm({ task_type: a.task_type, assigned_email: a.assigned_email, rate: a.rate, rate_type: a.rate_type || "per_unit", invoice_status: a.invoice_status });
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await axios.put(`${API}/assignments/${editTarget.id}`, editForm);
      toast.success("Updated");
      setEditTarget(null);
      fetchAssignments();
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  // Filter assignments
  const usedTypes = [...new Set(assignments.map(a => a.task_type).filter(Boolean))].sort();
  const filteredAssignments = assignments.filter(a => {
    if (assignFilter.task_type && a.task_type !== assignFilter.task_type) return false;
    if (assignFilter.search) {
      const s = assignFilter.search.toLowerCase();
      return (a.main_title || "").toLowerCase().includes(s)
        || (a.po_number || "").toLowerCase().includes(s)
        || (a.assigned_email || "").toLowerCase().includes(s)
        || (a.episode_title || "").toLowerCase().includes(s);
    }
    return true;
  });
  const totalAssignmentAmount = filteredAssignments.reduce((s, a) => s + (a.amount || 0), 0);

  return (
    <div className="page-container" data-testid="mini-assignments-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-purple-500" />
            Mini Assignments
          </h1>
          <p className="page-subtitle">Select parent tasks, assign child task types to resources</p>
        </div>
        <Button onClick={() => { fetchTasks(); fetchAssignments(); }} variant="outline" className="btn-secondary" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* ====== TASKS BROWSER ====== */}
      <Card className="card mb-4">
        <button
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
          onClick={() => setTasksExpanded(!tasksExpanded)}
          data-testid="tasks-section-toggle"
        >
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Browse & Select Tasks</h2>
            <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{allTasks.length} total</Badge>
            {selectedTaskIds.length > 0 && (
              <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">{selectedTaskIds.length} selected</Badge>
            )}
          </div>
          {tasksExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {tasksExpanded && (
          <CardContent className="p-0">
            {/* Filters */}
            <div className="px-4 py-2.5 border-t border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <select
                value={taskTypeFilter}
                onChange={e => setTaskTypeFilter(e.target.value)}
                className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-purple-500 min-w-[120px]"
                data-testid="task-type-filter"
              >
                <option value="">All Types</option>
                {allTaskTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={taskSearch}
                  onChange={e => setTaskSearch(e.target.value)}
                  placeholder="Search title, PO, job ID, resource..."
                  className="pl-8 h-8 text-xs"
                  data-testid="task-search"
                />
              </div>
              <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] px-2 py-0.5">{filteredTasks.length} records</Badge>
              <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px] px-2 py-0.5">
                {"\u20B9"}{filteredTasks.reduce((s, r) => s + (r.amount || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </Badge>
            </div>

            {/* Tasks Table */}
            {tasksLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-purple-500 animate-spin" /></div>
            ) : (
              <div className="max-h-[420px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 sticky top-0 z-10">
                      <TableHead className="w-10 px-2">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={toggleAllVisible}
                          data-testid="select-all-tasks"
                        />
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-[200px] px-2">Title / Episode</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase text-center w-20 px-1">Type</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase text-center w-24 px-1">PO</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase text-center w-28 px-1">Job ID</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-[160px] px-2">Resource</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase text-right w-16 px-1">Dur.</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase text-right w-14 px-1">Rate</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase text-right w-20 px-2">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTasks.map((t) => {
                      const isSelected = selectedTaskIds.includes(t.id);
                      return (
                        <TableRow
                          key={t.id}
                          className={`hover:bg-slate-50/50 cursor-pointer ${isSelected ? "bg-purple-50/40" : ""}`}
                          onClick={() => toggleTask(t.id)}
                          data-testid={`task-row-${t.id}`}
                        >
                          <TableCell className="px-2" onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleTask(t.id)}
                            />
                          </TableCell>
                          <TableCell className="px-2 py-1.5">
                            <p className="text-xs font-medium text-slate-700 leading-tight line-clamp-1" style={{ wordBreak: 'break-word' }}>{t.main_title || "-"}</p>
                            <p className="text-[10px] text-slate-400 leading-tight mt-0.5 line-clamp-1" style={{ wordBreak: 'break-word' }}>{t.episode_title || ""} {t.episode_number ? `Ep ${t.episode_number}` : ""}</p>
                          </TableCell>
                          <TableCell className="text-center px-1 py-1.5">
                            <span className="inline-flex px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-semibold">{t.task || "-"}</span>
                          </TableCell>
                          <TableCell className="text-center text-[11px] font-mono text-slate-600 px-1 py-1.5">{t.po_number || "-"}</TableCell>
                          <TableCell className="text-center text-[11px] font-mono text-slate-600 px-1 py-1.5">{t.atlas_job_id || "-"}</TableCell>
                          <TableCell className="text-[11px] text-slate-600 px-2 py-1.5 truncate max-w-[160px]">{t.resource || "-"}</TableCell>
                          <TableCell className="text-right text-[11px] font-mono text-slate-600 px-1 py-1.5">{(t.duration_words || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right text-[11px] font-mono text-slate-600 px-1 py-1.5">{"\u20B9"}{(t.rate || 0).toFixed(0)}</TableCell>
                          <TableCell className="text-right text-xs font-mono font-semibold text-emerald-600 px-2 py-1.5">{"\u20B9"}{(t.amount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredTasks.length === 0 && !tasksLoading && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-10 text-sm text-slate-400">No matching tasks</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ====== CREATE PANEL (sticky when tasks selected) ====== */}
      {showCreatePanel && selectedTaskIds.length > 0 && (
        <Card className="card mb-4 border-purple-200 bg-purple-50/30" data-testid="create-panel">
          <CardContent className="p-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-[120px]">
                <Badge className="bg-purple-600 text-white border-0 text-xs px-2.5 py-1">
                  {selectedTaskIds.length} task(s) selected
                </Badge>
              </div>
              <div className="space-y-1 min-w-[150px]">
                <Label className="text-[10px] text-slate-500 uppercase">Child Task Type</Label>
                <Select value={childTaskType} onValueChange={v => { setChildTaskType(v); setCustomRate(""); }}>
                  <SelectTrigger className="h-8 text-xs bg-white" data-testid="child-type-select">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[180px]">
                <Label className="text-[10px] text-slate-500 uppercase">Assign To</Label>
                <Select value={assignedEmail} onValueChange={v => { setAssignedEmail(v); setCustomRate(""); }}>
                  <SelectTrigger className="h-8 text-xs bg-white" data-testid="assign-email-select">
                    <SelectValue placeholder="Select resource..." />
                  </SelectTrigger>
                  <SelectContent>
                    {resourceEmails.map(email => (
                      <SelectItem key={email} value={email}>{email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 w-[100px]">
                <Label className="text-[10px] text-slate-500 uppercase">Rate (per unit)</Label>
                <Input
                  type="number"
                  value={customRate !== "" ? customRate : (assignedEmail && childTaskType ? getDefaultRate(assignedEmail, childTaskType) : "")}
                  onChange={e => setCustomRate(e.target.value)}
                  placeholder="Auto"
                  className="h-8 text-xs bg-white"
                  data-testid="rate-input"
                />
              </div>
              {assignedEmail && childTaskType && (
                <div className="text-[10px] text-slate-500 pb-1.5">
                  Default: {"\u20B9"}{getDefaultRate(assignedEmail, childTaskType)}/unit
                </div>
              )}
              <Button
                onClick={handleCreate}
                disabled={creating || !childTaskType || !assignedEmail}
                className="h-8 bg-purple-600 hover:bg-purple-700 text-white text-xs"
                data-testid="create-mini-btn"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                {creating ? "Creating..." : "Create Mini Assignments"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedTaskIds([]); setShowCreatePanel(false); }}
                className="h-8 text-xs text-slate-500"
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ====== EXISTING MINI ASSIGNMENTS ====== */}
      <Card className="card">
        <button
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
          onClick={() => setAssignmentsExpanded(!assignmentsExpanded)}
          data-testid="assignments-section-toggle"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-800">Mini Assignments ({filteredAssignments.length})</h2>
            <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">
              {"\u20B9"}{totalAssignmentAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </Badge>
          </div>
          {assignmentsExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {assignmentsExpanded && (
          <>
            {/* Filters */}
            <div className="px-4 py-2.5 border-t border-b border-slate-200 flex items-center gap-3 flex-wrap">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={assignFilter.task_type} onValueChange={v => setAssignFilter(p => ({ ...p, task_type: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {usedTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={assignFilter.search}
                  onChange={e => setAssignFilter(p => ({ ...p, search: e.target.value }))}
                  placeholder="Search project, PO, email..."
                  className="pl-8 h-8 text-xs"
                  data-testid="filter-search"
                />
              </div>
            </div>

            {/* Table */}
            {assignmentsLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-purple-500 animate-spin" /></div>
            ) : filteredAssignments.length > 0 ? (
              <div className="max-h-[400px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 sticky top-0 z-10">
                      <TableHead className="text-[10px] font-semibold uppercase">Project</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-20">PO</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-20 text-center">Child Type</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-[140px]">Assigned To</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-16 text-right">Dur.</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-14 text-right">Rate</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-20 text-right">Amount</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-20 text-center">Status</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase w-20 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssignments.map(a => (
                      <TableRow key={a.id} className="hover:bg-slate-50/50" data-testid={`mini-row-${a.id}`}>
                        <TableCell>
                          <p className="text-xs font-medium text-slate-700 line-clamp-1">{a.main_title || "-"}</p>
                          <p className="text-[10px] text-slate-400 line-clamp-1">{a.episode_title || ""}</p>
                        </TableCell>
                        <TableCell className="text-[11px] font-mono text-slate-600">{a.po_number || "-"}</TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-semibold">
                            {a.task_type || "-"}
                          </span>
                        </TableCell>
                        <TableCell className="text-[11px] text-slate-600 truncate max-w-[140px]">
                          {a.assigned_display_name || a.assigned_email || "-"}
                        </TableCell>
                        <TableCell className="text-right text-[11px] font-mono text-slate-600">{(a.duration_words || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-[11px] font-mono text-slate-600">{"\u20B9"}{(a.rate || 0).toFixed(0)}</TableCell>
                        <TableCell className="text-right text-xs font-mono font-semibold text-emerald-600">{"\u20B9"}{(a.amount || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={`text-[9px] border-0 ${
                            a.invoice_status === "invoiced" || a.invoice_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                            a.invoice_status === "ready" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                          }`}>
                            {a.invoice_status || "not_ready"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => openEdit(a)} className="p-1 rounded hover:bg-slate-100" data-testid={`edit-${a.id}`}>
                              <Pencil className="w-3 h-3 text-slate-500" />
                            </button>
                            <button onClick={() => setDeleteTarget(a)} className="p-1 rounded hover:bg-red-50" data-testid={`delete-${a.id}`}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No mini assignments yet</p>
                <p className="text-xs text-slate-400 mt-1">Select tasks above and create assignments</p>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base text-red-600">Delete Mini Assignment</DialogTitle>
            <DialogDescription>This will permanently remove this assignment.</DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="p-3 bg-slate-50 rounded-lg text-xs">
              <p className="font-medium text-slate-800">{deleteTarget.main_title}</p>
              <p className="text-slate-500 mt-0.5">{deleteTarget.task_type} &middot; {deleteTarget.assigned_email}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="w-4 h-4 text-purple-500" />
              Edit Mini Assignment
            </DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-xs">
                <p className="font-medium text-slate-800">{editTarget.main_title}</p>
                <p className="text-slate-500 mt-0.5">PO: {editTarget.po_number} &middot; {editTarget.episode_title}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Child Task Type</Label>
                  <Select value={editForm.task_type} onValueChange={v => setEditForm(p => ({ ...p, task_type: v }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Invoice Status</Label>
                  <Select value={editForm.invoice_status} onValueChange={v => setEditForm(p => ({ ...p, invoice_status: v }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_ready">Not Ready</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="invoiced">Invoiced</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Rate</Label>
                  <Input
                    type="number"
                    value={editForm.rate}
                    onChange={e => setEditForm(p => ({ ...p, rate: parseFloat(e.target.value) || 0 }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Assigned To</Label>
                  <Select value={editForm.assigned_email} onValueChange={v => setEditForm(p => ({ ...p, assigned_email: v }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {resourceEmails.map(email => (
                        <SelectItem key={email} value={email}>{email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveEdit} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-edit">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MiniAssignments;
